import { Rectangle } from '@shure/shared/angular/data-access/system-api/models';

export interface Point {
	x: number;
	y: number;
}

/**
 * Coordinates of two diagonally opposite corners
 * in a rectangular area
 */
export interface CoordinatePairRectangle {
	xMax: number;
	yMax: number;
	yMin: number;
	xMin: number;
}

/**
 * Maps the rectangle defined by the points for two diagonally opposite corners
 */
export function mapCoordinatesToRect(pointA: Point, pointB: Point): Rectangle {
	const center = getCenterPoint(pointA, pointB);

	const dimensions = getDimensions(pointA, pointB);

	return {
		...center,
		...dimensions
	};
}

/**
 * Calculates the center c between two points a(x,y) and b(x,y)
 */
export function getCenterPoint(pointA: Point, pointB: Point): Point {
	const pointC: Point = {
		x: (pointA.x + pointB.x) / 2,
		y: (pointA.y + pointB.y) / 2
	};
	return pointC;
}

/**
 * Calculate the dimensions of the rectangle spanning between two points
 */
export function getDimensions(pointA: Point, pointB: Point): { height: number; width: number } {
	return {
		height: pointA.y > pointB.y ? pointA.y - pointB.y : pointB.y - pointA.y,
		width: pointA.x > pointB.x ? pointA.x - pointB.x : pointB.x - pointA.x
	};
}

/**
 * Distance between two points a(x,y) and b(x,y)
 */
export function distance(pointA: Point, pointB: Point): number {
	const distance = length({ x: pointA.x - pointB.x, y: pointA.y - pointB.y });
	return distance;
}

/**
 * Calculates the length of a vector v=(x,y)
 */
export function length(vector: Point): number {
	const length = Math.sqrt(Math.pow(vector.x, 2) + Math.pow(vector.y, 2));
	return length;
}

/**
 * Check if two rectangles are overlapping earchother.
 */
export function doRectanglesOverlap(r1: CoordinatePairRectangle, r2: CoordinatePairRectangle): boolean {
	// We are gonna use a simple mathematical statement to figure out if the areas overlap.
	// The statement is based on rectangles presented as 2-pair coordinates, rather than 1-coordinate-width-height
	// adjust for topleft coordinate instead of center of square
	// given the coordinates whe use a mathematical equation to determine if the squares overlap
	const notOverlapping = r1.xMax < r2.xMin || r1.yMax < r2.yMin || r1.xMin > r2.xMax || r1.yMin > r2.yMax;

	return !notOverlapping;
}
